import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../../services/apiService';
import AuthService from '../../../services/authService';
import type { MappingDocumentRequest } from '../../../types/mappingDocument';
import type { CreatedJob } from '../GenerateBar';

const REDIRECT_SECONDS = 3;

/** Creates the mapping document job, then counts down and sends the user to the dashboard to watch it. */
export function useMappingJob() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedJob | null>(null);

  const countdown = created?.countdown;
  useEffect(() => {
    if (countdown === undefined) return;
    if (countdown <= 0) {
      navigate('/dashboard');
      return;
    }
    const timer = setTimeout(() => setCreated((prev) => prev && { ...prev, countdown: prev.countdown - 1 }), 1000);
    return () => clearTimeout(timer);
  }, [countdown, navigate]);

  const submit = useCallback(async (request: Omit<MappingDocumentRequest, 'username'>) => {
    setSubmitting(true);
    setError(null);
    try {
      const { username } = AuthService.getInstance().getAuthState();
      const response = await apiService.createMappingDocumentJob({ ...request, username: username || undefined });
      setCreated({ jobId: response.jobId, countdown: REDIRECT_SECONDS });
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to create mapping document job');
    } finally {
      setSubmitting(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return { submitting, error, setError, clearError, created, submit };
}
